import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { getLevels, getBadges, getMemberBadges } from '../../api';
import './Profil.css';

function Profil() {
  const { user, token } = useAuth();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allBadges, setAllBadges] = useState([]);
  const [earnedBadgeIds, setEarnedBadgeIds] = useState(new Set());
  const [earnedBadgesMap, setEarnedBadgesMap] = useState({});
  const [badgesLoading, setBadgesLoading] = useState(true);

  useEffect(() => {
    async function fetchLevels() {
      try {
        const response = await getLevels();
        setLevels(response.data.sort((a, b) => a.level - b.level));
      } catch (err) {
        console.error('Failed to load levels:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchLevels();
  }, []);

  useEffect(() => {
    async function fetchBadges() {
      try {
        const [allResponse, memberResponse] = await Promise.all([
          getBadges(),
          getMemberBadges(token),
        ]);
        setAllBadges(allResponse.data);
        const earned = {};
        memberResponse.data.forEach((b) => {
          earned[b.id] = b.obtained_at;
        });
        setEarnedBadgesMap(earned);
        setEarnedBadgeIds(new Set(memberResponse.data.map((b) => b.id)));
      } catch (err) {
        console.error('Failed to load badges:', err);
      } finally {
        setBadgesLoading(false);
      }
    }
    fetchBadges();
  }, [token]);

  const calculateProgress = () => {
    if (levels.length === 0) {
      return { percentage: 0, currentXP: 0, xpInCurrentLevel: 0, xpNeededForNextLevel: null };
    }

    const currentXP = user?.experience_points || 0;
    let currentLevel = levels[0];
    let nextLevel = levels[1] || null;

    for (let i = 0; i < levels.length; i++) {
      if (levels[i].experience_required <= currentXP) {
        currentLevel = levels[i];
        nextLevel = levels[i + 1] || null;
      } else {
        break;
      }
    }

    if (!nextLevel) {
      return { percentage: 100, currentXP, xpInCurrentLevel: 0, xpNeededForNextLevel: 0, isMaxLevel: true, currentLevel };
    }

    const xpInCurrentLevel = currentXP - currentLevel.experience_required;
    const xpNeededForNextLevel = nextLevel.experience_required - currentLevel.experience_required;
    const percentage = Math.min(100, Math.round((xpInCurrentLevel / xpNeededForNextLevel) * 100));

    return { percentage, currentXP, xpInCurrentLevel, xpNeededForNextLevel, currentLevel };
  };

  const progress = calculateProgress();
  const currentLevel = progress.currentLevel || user?.current_level;

  return (
    <div className="profil-page">

      {/* ── Hero ── */}
      <section className="profil-hero">
        <div className="profil-hero-avatar">
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </div>

        <div className="profil-hero-info">
          <div className="profil-hero-name-row">
            <h1>{user?.first_name} {user?.last_name}</h1>
            {currentLevel && (
              <span className="profil-level-badge">Niveau {currentLevel.level}</span>
            )}
          </div>

          {loading ? (
            <p className="profil-xp-loading">Calcul de l'expérience...</p>
          ) : (
            <>
              <div className="profil-xp-bar-track">
                <div
                  className="profil-xp-bar-fill"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <div className="profil-xp-labels">
                {progress.isMaxLevel ? (
                  <span className="profil-xp-max">Niveau maximum atteint !</span>
                ) : progress.xpNeededForNextLevel !== null ? (
                  <>
                    <span>{progress.xpInCurrentLevel} / {progress.xpNeededForNextLevel} XP</span>
                    <span>{progress.percentage}%</span>
                  </>
                ) : (
                  <span>{progress.currentXP} XP</span>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Badges ── */}
      <section>
        <h2 className="profil-section-title">Badges</h2>

        {badgesLoading ? (
          <p className="profil-loading">Chargement des badges...</p>
        ) : allBadges.length === 0 ? (
          <p className="profil-empty">Aucun badge disponible.</p>
        ) : (
          <div className="badges-grid">
            {allBadges.map((badge) => {
              const isEarned = earnedBadgeIds.has(badge.id);
              return (
                <div
                  key={badge.id}
                  className={`badge-card ${isEarned ? 'badge-earned' : 'badge-locked'}`}
                >
                  <div className="badge-image-wrapper">
                    {badge.image ? (
                      <img src={badge.image} alt={badge.name} className="badge-image" />
                    ) : (
                      <div className="badge-placeholder">
                        <span>{isEarned ? '★' : '?'}</span>
                      </div>
                    )}
                    {!isEarned && <div className="badge-lock-overlay" />}
                  </div>
                  <span className="badge-name">{badge.name}</span>
                  <span className="badge-description">{badge.description}</span>
                  {isEarned && earnedBadgesMap[badge.id] && (
                    <span className="badge-date">
                      {new Date(earnedBadgesMap[badge.id]).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

    </div>
  );
}

export default Profil;
